import { QueryKey } from '@api'
import { ApiError } from '@api/model'
import { useApi } from '@api/useApi.hook'
import { appConfig } from '@config'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query-v4'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import {
  PreIpoInterestRegistration,
  RegisterPreIpoInterestInput,
  UpcomingWhitelistEligibility,
} from '../types/preIpoInterest.types'

export const useMyPreIpoInterest = (fundUrlId: string) => {
  const isAuthenticated = useIsAuthenticated()
  const { get } = useApi()

  return useQuery({
    queryKey: [QueryKey.PreIpoInterest, fundUrlId],
    queryFn: async () => {
      try {
        const response = await get<{ data: PreIpoInterestRegistration }>(
          appConfig.endpoints.v1.preIpoInterest.myRegistration.replace(
            ':fundUrlId',
            fundUrlId,
          ),
        )()
        return response.data
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: isAuthenticated && !!fundUrlId,
    retry: false,
  })
}

export type WhitelistEligibilityContext = 'upcoming' | 'liveInvest'

export const useUpcomingWhitelistEligibility = (
  fundUrlId: string,
  options?: { enabled?: boolean; context?: WhitelistEligibilityContext },
) => {
  const isAuthenticated = useIsAuthenticated()
  const { get } = useApi()
  const context = options?.context ?? 'upcoming'

  return useQuery({
    queryKey: [QueryKey.PreIpoInterest, 'eligibility', fundUrlId, context],
    queryFn: async () => {
      const response = await get<{ data: UpcomingWhitelistEligibility }>(
        appConfig.endpoints.v1.preIpoInterest.eligibility.replace(
          ':fundUrlId',
          fundUrlId,
        ),
        {
          query: context === 'liveInvest' ? { context: 'liveInvest' } : undefined,
        },
      )()
      return response.data
    },
    enabled: (options?.enabled ?? true) && isAuthenticated && !!fundUrlId,
    retry: false,
  })
}

export const useRegisterPreIpoInterest = (fundUrlId: string) => {
  const { post } = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: RegisterPreIpoInterestInput) => {
      const response = await post<{ data: PreIpoInterestRegistration }>(
        appConfig.endpoints.v1.preIpoInterest.main,
        { disableAutoSnakeCase: true },
      )(input)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PreIpoInterest, fundUrlId] })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PreIpoInterest, 'eligibility', fundUrlId],
      })
    },
  })
}
